import { useEffect, useState } from "react";
import { apiDelete, apiGet } from "../api";
import { LinkButton } from "./LinkButton";

interface DeviceDto {
  hwid: string;
  platform: string | null;
  deviceModel: string | null;
  osVersion: string | null;
  createdAt: string;
  deletable: boolean;
  daysUntilDeletable: number;
}

function deviceLabel(device: DeviceDto, labels: Record<string, string>): string {
  return device.deviceModel ?? device.platform ?? labels.unknownDevice ?? "?";
}

export function DeviceList({ labels }: { labels: Record<string, string> }) {
  const [devices, setDevices] = useState<DeviceDto[] | null>(null);
  const [pending, setPending] = useState<DeviceDto | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet<DeviceDto[]>("/api/devices")
      .then(setDevices)
      .catch(() => setDevices([]));
  }, []);

  async function confirmDelete() {
    if (!pending) return;
    setBusy(true);
    try {
      await apiDelete(`/api/devices/${pending.hwid}`);
      const deletedHwid = pending.hwid;
      setDevices((prev) => prev?.filter((d) => d.hwid !== deletedHwid) ?? null);
      setPending(null);
    } finally {
      setBusy(false);
    }
  }

  if (!devices) return <p className="text-text-muted">…</p>;

  return (
    <div>
      {devices.length === 0 && <p className="text-text-muted">{labels.noDevices}</p>}
      <ul>
        {devices.map((device) => (
          <li key={device.hwid} className="flex items-center justify-between gap-3 py-2 border-b border-border">
            <div>
              <div>{deviceLabel(device, labels)}</div>
              <div className="text-text-muted">
                {labels.addedOn} {new Date(device.createdAt).toLocaleDateString()}
              </div>
            </div>
            {device.deletable ? (
              <LinkButton onClick={() => setPending(device)}>{labels.deleteButton}</LinkButton>
            ) : (
              <span className="text-text-muted">
                {(labels.deviceTooNew ?? "").replace("{days}", String(device.daysUntilDeletable))}
              </span>
            )}
          </li>
        ))}
      </ul>

      {pending && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          onClick={() => !busy && setPending(null)}
        >
          <div className="bg-bg border border-border p-4 max-w-[320px] w-full" onClick={(e) => e.stopPropagation()}>
            <p>{labels.deleteDeviceTitle}</p>
            <p className="text-text-muted">{deviceLabel(pending, labels)}</p>
            <div className="flex gap-4 mt-4">
              <LinkButton disabled={busy} onClick={confirmDelete}>
                {labels.confirmButton}
              </LinkButton>
              <LinkButton disabled={busy} onClick={() => setPending(null)}>
                {labels.cancelButton}
              </LinkButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
